import React from "react";
import { ExternalLink } from "lucide-react";

/**
 * Smart renderer for an arbitrary Firestore field value. Used by the
 * read-only listing detail view.
 *
 * Handles:
 *   - null / undefined / empty string → "—" placeholder
 *   - booleans → "Yes" / "No"
 *   - numbers → locale-formatted
 *   - Firestore Timestamps (both client SDK objects and `_seconds` shape)
 *   - ISO date strings → locale-formatted
 *   - image URLs → thumbnail + "Open" link
 *   - other URLs → safe external link
 *   - arrays → bulleted list, recursively rendered
 *   - plain objects → key/value pairs, recursively rendered
 *   - long strings → wrap, preserve whitespace
 */
const IMAGE_EXT_RE = /\.(jpg|jpeg|png|webp|gif|avif|svg)(\?|$)/i;
const FIREBASE_STORAGE_RE = /firebasestorage\.googleapis\.com/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|$)/;
const URL_RE = /^https?:\/\//i;

function formatDate(d) {
  if (!d) return "";
  const parsed = d instanceof Date ? d : new Date(d);
  if (isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function isImageUrl(s) {
  return IMAGE_EXT_RE.test(s) || FIREBASE_STORAGE_RE.test(s);
}

function Placeholder() {
  return <span className="text-gray-300 italic">—</span>;
}

export default function FieldValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return <Placeholder />;
  }

  if (typeof value === "boolean") {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
          value
            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
            : "bg-gray-50 text-gray-600 ring-1 ring-gray-200"
        }`}
      >
        {value ? "Yes" : "No"}
      </span>
    );
  }

  if (typeof value === "number") {
    return <span className="cms-tabular">{value.toLocaleString()}</span>;
  }

  // Firestore Timestamp shapes
  if (value && typeof value === "object") {
    if (typeof value.toDate === "function") {
      return <span>{formatDate(value.toDate())}</span>;
    }
    if (typeof value.seconds === "number" || typeof value._seconds === "number") {
      const secs = value.seconds ?? value._seconds;
      return <span>{formatDate(new Date(secs * 1000))}</span>;
    }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-400 italic">(empty list)</span>;
    }
    // All-image-URL array → render as thumbnail grid
    const allImages = value.every(
      (v) => typeof v === "string" && URL_RE.test(v) && isImageUrl(v),
    );
    if (allImages) {
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((url, i) => (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src={url}
                alt=""
                className="w-20 h-20 object-cover rounded-md ring-1 ring-gray-200"
              />
            </a>
          ))}
        </div>
      );
    }
    return (
      <ul className="list-disc list-inside space-y-1">
        {value.map((v, i) => (
          <li key={i} className="text-sm text-gray-700">
            <FieldValue value={v} />
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return <span className="text-gray-400 italic">(empty)</span>;
    }
    return (
      <div className="space-y-1.5 border-l-2 border-gray-100 pl-3">
        {entries.map(([k, v]) => (
          <div key={k} className="text-sm">
            <span className="text-gray-500 font-medium">{k}:</span>{" "}
            <FieldValue value={v} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "string") {
    if (URL_RE.test(value)) {
      if (isImageUrl(value)) {
        return (
          <div className="space-y-1">
            <img
              src={value}
              alt=""
              className="max-w-xs max-h-48 object-contain rounded-md ring-1 ring-gray-200"
            />
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-leechiuBlue hover:underline"
            >
              Open <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        );
      }
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-start gap-1 text-leechiuBlue hover:underline break-all"
        >
          <span>{value}</span>
          <ExternalLink className="w-3 h-3 mt-1 shrink-0" />
        </a>
      );
    }
    if (ISO_DATE_RE.test(value)) {
      const formatted = formatDate(value);
      if (formatted) return <span>{formatted}</span>;
    }
    // Long string → preserve whitespace, wrap
    if (value.length > 120 || value.includes("\n")) {
      return (
        <div className="whitespace-pre-wrap text-sm text-gray-700">{value}</div>
      );
    }
    return <span className="text-gray-700">{value}</span>;
  }

  return <span>{String(value)}</span>;
}
